import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, MapPin, ArrowRight, Linkedin, Instagram, Youtube, Twitter } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { submitContact } from "@/lib/api";

export default function Contact() {
  const { t, isRTL } = useI18n();
  const f = t("contact.form");
  const emirates = t("early.form.emirates");

  const [form, setForm] = useState({
    full_name: "", email: "", mobile: "", enquiry_type: "",
    company: "", emirate: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.mobile || !form.enquiry_type || !form.message) {
      toast.error(t("common.required"));
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success(f.success, { id: "contact-success" });
      setForm({ full_name: "", email: "", mobile: "", enquiry_type: "", company: "", emirate: "", message: "" });
    } catch {
      toast.error(t("business.form.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section bg-white" data-testid="section-contact">
      <div className="container-x">
        <div className="max-w-3xl">
          <div className="eyebrow">{t("contact.eyebrow")}</div>
          <h2 className="h2 mt-4">{t("contact.title")}</h2>
          <p className="lede mt-5">{t("contact.lede")}</p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 rounded-3xl bg-brand-charcoal text-white p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -top-16 -end-10 h-52 w-52 rounded-full bg-brand-green/25 blur-3xl" />
            <div className="text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold">
              {t("contact.eyebrow")}
            </div>
            <div className="mt-3 text-2xl font-extrabold leading-tight">
              {t("contact.title")}
            </div>

            <div className="mt-8 space-y-4 text-sm">
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-brand-gold" />
                </span>
                <span className="text-white/85">{t("contact.location")}</span>
              </div>
              <a href="mailto:Info@raknati.com" className="flex items-center gap-3 group" data-testid="contact-email-link">
                <span className="h-9 w-9 rounded-full bg-white/10 group-hover:bg-brand-gold group-hover:text-brand-charcoal transition-colors duration-200 flex items-center justify-center">
                  <Mail className="h-4 w-4 text-brand-gold group-hover:text-brand-charcoal" />
                </span>
                <span className="text-white/85 group-hover:text-white transition-colors duration-200">Info@raknati.com</span>
              </a>
              <a href="https://wa.me/971525996630" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group" data-testid="contact-whatsapp-link">
                <span className="h-9 w-9 rounded-full bg-white/10 group-hover:bg-brand-gold group-hover:text-brand-charcoal transition-colors duration-200 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-brand-gold group-hover:fill-brand-charcoal" aria-hidden="true"><path d="M20.52 3.48A11.94 11.94 0 0 0 12 0C5.37 0 .01 5.37.01 12c0 2.12.56 4.13 1.62 5.93L0 24l6.24-1.63A11.94 11.94 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.19-1.25-6.19-3.48-8.52ZM12 21.82c-1.93 0-3.83-.52-5.47-1.5l-.39-.23-3.7.97.99-3.61-.25-.4A9.83 9.83 0 1 1 12 21.82Zm5.4-7.36c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15s-.77.96-.94 1.16c-.17.2-.35.23-.65.08-.3-.15-1.24-.46-2.37-1.46-.88-.79-1.47-1.76-1.64-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49s1.07 2.89 1.22 3.09c.15.2 2.1 3.2 5.08 4.49.71.31 1.27.49 1.7.63.71.23 1.36.2 1.87.12.57-.08 1.75-.71 2-1.39.25-.68.25-1.26.17-1.39-.08-.13-.27-.2-.57-.35Z"/></svg>
                </span>
                <span className="text-white/85 group-hover:text-white transition-colors duration-200">+971 52 599 6630</span>
              </a>
            </div>

            <div className="mt-8">
              <div className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold">
                {t("contact.social")}
              </div>
              <div className="mt-3 flex items-center gap-2">
                {[
                  { icon: Linkedin, label: "LinkedIn" },
                  { icon: Instagram, label: "Instagram" },
                  { icon: Youtube, label: "YouTube" },
                  { icon: Twitter, label: "X" },
                ].map(({ icon: Icon, label }) => (
                  <a key={label} href="#" aria-label={label}
                    className="h-10 w-10 rounded-full bg-white/10 hover:bg-brand-gold hover:text-brand-charcoal transition-colors duration-200 flex items-center justify-center"
                    data-testid={`contact-social-${label.toLowerCase()}`}>
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-2">
              {t("contact.categories").map((c) => (
                <span key={c} className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-white/10 border border-white/15">
                  {c}
                </span>
              ))}
            </div>
          </div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-3 rounded-3xl border border-brand-line bg-white p-8 md:p-10"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={f.full_name} value={form.full_name} onChange={update("full_name")} testid="contact-full-name" required />
              <Field label={f.email} type="email" value={form.email} onChange={update("email")} testid="contact-email" required />
              <Field label={f.mobile} value={form.mobile} onChange={update("mobile")} testid="contact-mobile" required />
              <Select label={f.enquiry_type} value={form.enquiry_type} onChange={update("enquiry_type")}
                placeholder={f.enquiry_type_ph} options={t("contact.categories")} testid="contact-enquiry-type" required />
              <Field label={f.company} value={form.company} onChange={update("company")} testid="contact-company" />
              <Select label={f.emirate} value={form.emirate} onChange={update("emirate")}
                placeholder={t("early.form.emirate_ph")} options={emirates} testid="contact-emirate" />
              <div className="md:col-span-2">
                <TextArea label={f.message} value={form.message} onChange={update("message")} testid="contact-message" required />
              </div>
            </div>
            <button type="submit" disabled={submitting} className="mt-6 btn-primary w-full md:w-auto" data-testid="contact-submit">
              {submitting ? t("common.submitting") : f.submit}
              {!submitting && <ArrowRight className={`h-4 w-4 ${isRTL ? "rotate-180" : ""}`} />}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-brand-charcoal/70 mb-1.5">
        {label} {required && <span className="text-brand-red">*</span>}
      </span>
      <input type={type} value={value} onChange={onChange} required={required} data-testid={testid}
        className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm text-brand-charcoal placeholder:text-brand-charcoal/40 outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors duration-150" />
    </label>
  );
}
function TextArea({ label, value, onChange, testid, required }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-brand-charcoal/70 mb-1.5">
        {label} {required && <span className="text-brand-red">*</span>}
      </span>
      <textarea rows={5} value={value} onChange={onChange} required={required} data-testid={testid}
        className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm text-brand-charcoal placeholder:text-brand-charcoal/40 outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors duration-150 resize-none" />
    </label>
  );
}
function Select({ label, value, onChange, options, placeholder, testid, required }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-brand-charcoal/70 mb-1.5">
        {label} {required && <span className="text-brand-red">*</span>}
      </span>
      <select value={value} onChange={onChange} required={required} data-testid={testid}
        className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm text-brand-charcoal outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors duration-150">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
